import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Megaphone, Plus, Trash2, Edit3, Eye, EyeOff, Globe, MapPin, Calendar,
  FileText, Upload, X, ChevronDown, ChevronUp, Copy, ExternalLink,
  Users, Loader2, Check, AlertCircle, File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'trade_mission', label: 'Trade Mission', icon: '🌍' },
  { value: 'investment_opportunity', label: 'Investment Opportunity', icon: '💰' },
  { value: 'product_launch', label: 'Product Launch', icon: '🚀' },
  { value: 'delegation_visit', label: 'Delegation Visit', icon: '🤝' },
  { value: 'partnership', label: 'Partnership', icon: '🔗' },
  { value: 'exhibition_trade_show', label: 'Exhibition / Trade Show', icon: '🏛' },
  { value: 'announcement', label: 'Announcement', icon: '📢' },
  { value: 'pharma_park', label: 'Pharma Park / SEZ', icon: '🏭' },
  { value: 'infrastructure', label: 'Infrastructure Project', icon: '🏗' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const STATUS_BADGES = {
  draft: { label: 'Draft', cls: 'bg-zinc-700 text-zinc-300' },
  published: { label: 'Published', cls: 'bg-emerald-500/20 text-emerald-400' },
  closed: { label: 'Closed', cls: 'bg-red-500/20 text-red-400' },
};

export default function OpportunityManager({ profileId, profileSlug, isAdmin }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/profiles/${profileId}/showcase-opportunities`, { withCredentials: true });
      setOpportunities(res.data);
    } catch { }
    setLoading(false);
  }, [profileId]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  const handleDelete = async (oppId) => {
    if (!window.confirm('Delete this opportunity page? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/profiles/${profileId}/showcase-opportunities/${oppId}`, { withCredentials: true });
      setOpportunities(prev => prev.filter(o => o.id !== oppId));
    } catch { alert('Failed to delete'); }
  };

  const handleStatusToggle = async (opp) => {
    const next = opp.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API_URL}/api/profiles/${profileId}/showcase-opportunities/${opp.id}`, { status: next }, { withCredentials: true });
      setOpportunities(prev => prev.map(o => o.id === opp.id ? { ...o, status: next } : o));
    } catch { alert('Failed to update status'); }
  };

  const copyLink = (opp) => {
    const url = `${window.location.origin}/p/${profileSlug}/opportunity/${opp.slug}`;
    navigator.clipboard.writeText(url);
    alert('Link copied!');
  };

  if (loading) return <div className="animate-pulse bg-zinc-800 rounded-lg h-20" />;

  return (
    <div data-testid="opportunity-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-[#C41E3A]" />
          Opportunity Showcase
          <span className="text-zinc-500 text-xs font-normal">({opportunities.length})</span>
        </h3>
        <button
          onClick={() => { setShowCreate(true); setEditingId(null); }}
          className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#a31830] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          data-testid="create-opportunity-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add Opportunity
        </button>
      </div>

      <p className="text-zinc-500 text-xs mb-4">
        Create opportunity pages (trade missions, investments, product launches) visitors can respond to directly.
      </p>

      {/* Opportunity List */}
      {opportunities.length === 0 && !showCreate && (
        <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
          <Megaphone className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No opportunity pages yet</p>
          <button
            onClick={() => setShowCreate(true)}
            className="text-[#C41E3A] text-sm mt-2 hover:underline"
          >
            Create your first one
          </button>
        </div>
      )}

      <div className="space-y-3">
        {opportunities.map(opp => (
          <div key={opp.id} className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden" data-testid={`opportunity-card-${opp.id}`}>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGES[opp.status]?.cls || 'bg-zinc-700 text-zinc-300'}`}>
                      {STATUS_BADGES[opp.status]?.label || opp.status}
                    </span>
                    <span className="text-zinc-500 text-[10px]">
                      {CATEGORIES.find(c => c.value === opp.category)?.label || opp.category}
                    </span>
                  </div>
                  <h4 className="text-white font-semibold text-sm truncate">{opp.title}</h4>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-500">
                    {opp.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.location}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{opp.response_count || 0} responses</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{opp.view_count || 0} views</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleStatusToggle(opp)} className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title={opp.status === 'published' ? 'Unpublish' : 'Publish'}>
                    {opp.status === 'published' ? <EyeOff className="w-3.5 h-3.5 text-zinc-400" /> : <Eye className="w-3.5 h-3.5 text-emerald-400" />}
                  </button>
                  <button onClick={() => copyLink(opp)} className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title="Copy link">
                    <Copy className="w-3.5 h-3.5 text-zinc-400" />
                  </button>
                  {opp.status === 'published' && (
                    <a href={`/p/${profileSlug}/opportunity/${opp.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title="Preview">
                      <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />
                    </a>
                  )}
                  <button onClick={() => { setEditingId(opp.id); setShowCreate(true); }} className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title="Edit">
                    <Edit3 className="w-3.5 h-3.5 text-zinc-400" />
                  </button>
                  <button onClick={() => handleDelete(opp.id)} className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title="Delete">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>

              {/* Responses expandable */}
              {(opp.response_count || 0) > 0 && (
                <button
                  onClick={() => setExpandedId(expandedId === opp.id ? null : opp.id)}
                  className="flex items-center gap-1 mt-2 text-xs text-[#C41E3A] hover:underline"
                  data-testid={`view-responses-${opp.id}`}
                >
                  {expandedId === opp.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  View {opp.response_count} response{opp.response_count > 1 ? 's' : ''}
                </button>
              )}
            </div>

            {expandedId === opp.id && <ResponseList profileId={profileId} oppId={opp.id} />}
          </div>
        ))}
      </div>

      {/* Create/Edit Form */}
      {showCreate && (
        <OpportunityForm
          profileId={profileId}
          editingId={editingId}
          existing={editingId ? opportunities.find(o => o.id === editingId) : null}
          onClose={() => { setShowCreate(false); setEditingId(null); }}
          onSaved={() => { setShowCreate(false); setEditingId(null); fetchOpportunities(); }}
        />
      )}
    </div>
  );
}

function ResponseList({ profileId, oppId }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/profiles/${profileId}/showcase-opportunities/${oppId}/responses`, { withCredentials: true })
      .then(r => setResponses(r.data.responses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profileId, oppId]);

  if (loading) return <div className="px-4 pb-4"><Loader2 className="w-4 h-4 animate-spin text-zinc-500" /></div>;

  return (
    <div className="border-t border-zinc-700 bg-zinc-900/50 px-4 py-3 space-y-2" data-testid="responses-list">
      {responses.map((r, i) => (
        <div key={r.id || i} className="bg-zinc-800/50 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{r.name}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${r.rsvp_status === 'declined' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                {r.rsvp_status === 'declined' ? 'Declined' : 'Interested'}
              </span>
            </div>
            <span className="text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-zinc-400">{r.company} · {r.designation || 'N/A'}</p>
          <p className="text-zinc-400">{r.email} · {r.phone || 'N/A'}</p>
          {r.message && <p className="text-zinc-300 mt-1 italic">"{r.message}"</p>}
        </div>
      ))}
    </div>
  );
}

function OpportunityForm({ profileId, editingId, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: existing?.title || '',
    category: existing?.category || 'trade_mission',
    overview: existing?.overview || '',
    sections: existing?.sections || [{ title: '', content: '' }],
    key_dates: existing?.key_dates || { event_date: '', deadline: '' },
    location: existing?.location || '',
    geography: existing?.geography || '',
    documents: existing?.documents || [],
    contact_name: existing?.contact_name || '',
    contact_email: existing?.contact_email || '',
    contact_phone: existing?.contact_phone || '',
    hero_image: existing?.hero_image || '',
    inviter_title: existing?.inviter_title || '',
    inviter_name: existing?.inviter_name || '',
    confirmation_email_subject: existing?.confirmation_email_subject || '',
    confirmation_email_message: existing?.confirmation_email_message || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const updateField = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const addSection = () => setForm(p => ({ ...p, sections: [...p.sections, { title: '', content: '' }] }));
  const removeSection = (idx) => setForm(p => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));
  const updateSection = (idx, field, val) => setForm(p => ({
    ...p,
    sections: p.sections.map((s, i) => i === idx ? { ...s, [field]: val } : s)
  }));

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (form.documents.length + files.length > 10) {
      alert('Maximum 10 documents allowed');
      return;
    }
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/api/profiles/${profileId}/showcase-opportunities/upload-document`, fd, { withCredentials: true });
        setForm(p => ({
          ...p,
          documents: [...p.documents, { name: res.data.name, url: res.data.url, size: res.data.size, type: res.data.type }]
        }));
      } catch (err) {
        alert(`Failed to upload ${file.name}: ${err.response?.data?.detail || 'Unknown error'}`);
      }
    }
    setUploading(false);
  };

  const removeDoc = (idx) => setForm(p => ({ ...p, documents: p.documents.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.overview.trim()) {
      alert('Title and overview are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sections: form.sections.filter(s => s.title.trim() || s.content.trim()),
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/profiles/${profileId}/showcase-opportunities/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/profiles/${profileId}/showcase-opportunities`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="opportunity-form-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-white font-bold text-lg">
            {editingId ? 'Edit Opportunity' : 'New Opportunity Page'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded"><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Category */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">Category</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  onClick={() => updateField('category', c.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${form.category === c.value ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white' : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'}`}
                  data-testid={`category-${c.value}`}
                >
                  <span>{c.icon}</span> {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">Title *</label>
            <input
              value={form.title}
              onChange={e => updateField('title', e.target.value)}
              placeholder="e.g. India Pharma Park Investment Opportunity"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none"
              data-testid="opportunity-title-input"
            />
          </div>

          {/* Overview */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">Overview *</label>
            <textarea
              value={form.overview}
              onChange={e => updateField('overview', e.target.value)}
              rows={4}
              placeholder="Describe the opportunity in detail..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none resize-none"
              data-testid="opportunity-overview-input"
            />
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-zinc-400">Detail Sections</label>
              <button onClick={addSection} className="text-xs text-[#C41E3A] hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Section
              </button>
            </div>
            {form.sections.map((s, i) => (
              <div key={i} className="bg-zinc-800/30 border border-zinc-700/50 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={s.title}
                    onChange={e => updateSection(i, 'title', e.target.value)}
                    placeholder="Section title (e.g. Eligibility, Benefits)"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs focus:border-[#C41E3A] focus:outline-none"
                  />
                  <button onClick={() => removeSection(i)} className="p-1 hover:bg-zinc-700 rounded">
                    <X className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                </div>
                <textarea
                  value={s.content}
                  onChange={e => updateSection(i, 'content', e.target.value)}
                  rows={3}
                  placeholder="Section content..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs focus:border-[#C41E3A] focus:outline-none resize-none"
                />
              </div>
            ))}
          </div>

          {/* Key Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1.5">Event Date</label>
              <input
                type="date"
                value={form.key_dates.event_date || ''}
                onChange={e => updateField('key_dates', { ...form.key_dates, event_date: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none"
                data-testid="opportunity-event-date"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1.5">Response Deadline</label>
              <input
                type="date"
                value={form.key_dates.deadline || ''}
                onChange={e => updateField('key_dates', { ...form.key_dates, deadline: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none"
                data-testid="opportunity-deadline"
              />
            </div>
          </div>

          {/* Location / Geography */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1.5">Location</label>
              <input
                value={form.location}
                onChange={e => updateField('location', e.target.value)}
                placeholder="e.g. Hyderabad, India"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1.5">Geography / Region</label>
              <input
                value={form.geography}
                onChange={e => updateField('geography', e.target.value)}
                placeholder="e.g. South Asia, Global"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none"
              />
            </div>
          </div>

          {/* Documents */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">
              Supporting Documents <span className="text-zinc-600">({form.documents.length}/10)</span>
            </label>
            <div className="space-y-2 mb-2">
              {form.documents.map((doc, i) => (
                <div key={i} className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2 text-xs">
                  <File className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white truncate flex-1">{doc.name}</a>
                  <span className="text-zinc-600">{(doc.size / 1024).toFixed(0)}KB</span>
                  <button onClick={() => removeDoc(i)} className="p-0.5 hover:bg-zinc-700 rounded"><X className="w-3 h-3 text-zinc-500" /></button>
                </div>
              ))}
            </div>
            {form.documents.length < 10 && (
              <label className="flex items-center gap-2 bg-zinc-800 border border-dashed border-zinc-600 rounded-lg px-4 py-3 cursor-pointer hover:border-zinc-500 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" /> : <Upload className="w-4 h-4 text-zinc-400" />}
                <span className="text-zinc-400 text-xs">{uploading ? 'Uploading...' : 'Upload PDF, Word doc, or image (max 10MB)'}</span>
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" multiple onChange={handleDocUpload} disabled={uploading} />
              </label>
            )}
          </div>

          {/* Contact */}
          <div>
            <label className="text-xs text-zinc-400 block mb-2">Contact Information</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input value={form.contact_name} onChange={e => updateField('contact_name', e.target.value)} placeholder="Contact name" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none" />
              <input value={form.contact_email} onChange={e => updateField('contact_email', e.target.value)} placeholder="Email" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none" />
              <input value={form.contact_phone} onChange={e => updateField('contact_phone', e.target.value)} placeholder="Phone" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none" />
            </div>
          </div>

          {/* Inviter Details */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">
              Invited By <span className="text-zinc-600">(shown on the invitation page)</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              <select
                value={form.inviter_title}
                onChange={e => updateField('inviter_title', e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none"
                data-testid="inviter-title-select"
              >
                <option value="">Title</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
                <option value="Hon.">Hon.</option>
              </select>
              <input
                value={form.inviter_name}
                onChange={e => updateField('inviter_name', e.target.value)}
                placeholder="Full name of the inviting person"
                className="col-span-3 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none"
                data-testid="inviter-name-input"
              />
            </div>
          </div>

          {/* Confirmation Email Settings */}
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-lg p-4">
            <label className="text-xs text-zinc-400 block mb-2 font-medium">
              RSVP Confirmation Email <span className="text-zinc-600">(auto-sent when someone expresses interest)</span>
            </label>
            <div className="space-y-2">
              <input
                value={form.confirmation_email_subject}
                onChange={e => updateField('confirmation_email_subject', e.target.value)}
                placeholder="Subject (default: Confirmation: Your interest in [Title])"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none"
                data-testid="confirmation-subject-input"
              />
              <textarea
                value={form.confirmation_email_message}
                onChange={e => updateField('confirmation_email_message', e.target.value)}
                rows={3}
                placeholder="Custom message (default: Thank you for expressing your interest. We have received your RSVP and the organizer will be in touch shortly.)"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C41E3A] focus:outline-none resize-none"
                data-testid="confirmation-message-input"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-700 px-6 py-4 flex items-center justify-between">
          <button onClick={onClose} className="text-zinc-400 text-sm hover:text-white">Cancel</button>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !form.title.trim() || !form.overview.trim()}
              className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a31830] disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="save-opportunity-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingId ? 'Update' : 'Save as Draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
